import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { heritageColors } from '../../../../heritageTheme.ts';
import { getIconById } from '../../../NitzavSpotlightWidget/iconOptions';
import {
  PARNAS_TYPE_LABELS,
  SLOT_TYPE_LABELS,
} from '../../parnasConstants';

/**
 * HonorCard — כרטיס כבוד לפרנס בודד
 * תצוגה מלאה, עשירה ומכובדת לפרנס יחיד.
 * שמו מוצג בולט במרכז, וההערה (הטקסט הנוסף) מוצגת בולט ומרכזית —
 * בניגוד לתבניות אחרות שבהן ההערה קטנה ואיטלית.
 *
 * Props:
 *   honorCardPriority  'auto' | 'daily' | 'weekly' | 'monthly'
 *     auto  — בוחר לפי סדר עדיפויות: יומי → שבועי → חודשי
 */

const GOLD    = '#b8860b';
const GOLD_LT = '#d4a017';

const HonorCard = ({
  activeSponsors        = [],
  parnasType            = 'maor',
  parnasTypeCustomLabel = '',
  parnasTypeIcon        = 'star',
  currentParshaName     = '',
  todayHebrewLabel      = '',
  title                 = '',
  showTitle             = false,
  placeholderName       = 'השם שלך כאן',
  honorCardPriority     = 'auto',
}) => {
  const TypeIcon  = getIconById(parnasTypeIcon || 'star');
  const typeLabel = parnasType === 'acher' && parnasTypeCustomLabel
    ? parnasTypeCustomLabel
    : (PARNAS_TYPE_LABELS[parnasType] || '');

  // ── Pick which sponsor to display ─────────────────────────────────────────
  let sponsor = null;
  if (honorCardPriority === 'auto') {
    sponsor =
      activeSponsors.find(s => s.slotType === 'daily')   ||
      activeSponsors.find(s => s.slotType === 'weekly')  ||
      activeSponsors.find(s => s.slotType === 'monthly') ||
      null;
  } else {
    sponsor = activeSponsors.find(s => s.slotType === honorCardPriority) || null;
  }

  const slotLabel = sponsor
    ? (SLOT_TYPE_LABELS[sponsor.slotType] || '')
    : '';

  const contextLine =
    sponsor?.slotType === 'weekly'  && currentParshaName
      ? currentParshaName
    : sponsor?.slotType === 'monthly' && sponsor && todayHebrewLabel
      ? todayHebrewLabel
    : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box
      dir="rtl"
      sx={{
        width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Outer gold top stripe */}
      <Box sx={{
        height: '4px', flexShrink: 0,
        background: `linear-gradient(to left, transparent, ${GOLD} 20%, ${GOLD_LT} 50%, ${GOLD} 80%, transparent)`,
      }} />

      {/* Header */}
      <Box sx={{
        px: 2.5, pt: 1.5, pb: 1, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <TypeIcon sx={{ fontSize: 22, color: heritageColors.gold }} />
          <Typography variant="h6" sx={{ color: heritageColors.navy, fontWeight: 700 }}>
            {showTitle && title ? title : `פרנסי ${typeLabel}`}
          </Typography>
        </Box>
        {todayHebrewLabel && (
          <Typography sx={{ color: heritageColors.slate, fontSize: '0.75em', opacity: 0.65 }}>
            {todayHebrewLabel}
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: heritageColors.border, flexShrink: 0 }} />

      {/* Main card */}
      <Box sx={{
        flex: 1, minHeight: 0,
        m: 1.5,
        display: 'flex', flexDirection: 'column',
        border: `2px solid ${GOLD}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #fffdf7 0%, #fef8e4 45%, #fdf0c0 100%)',
        boxShadow: '0 6px 30px rgba(184,134,11,0.25), 0 2px 8px rgba(0,0,0,0.07)',
        position: 'relative',
      }}>

        {/* Top gold accent stripe */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
          background: `linear-gradient(to left, transparent, ${GOLD} 20%, ${GOLD_LT} 50%, ${GOLD} 80%, transparent)`,
          flexShrink: 0,
        }} />

        {/* Corner ornaments */}
        <Box sx={{
          position: 'absolute', top: '12px', right: '14px',
          width: '18px', height: '18px',
          borderTop: `2px solid ${GOLD}`, borderRight: `2px solid ${GOLD}`,
          opacity: 0.45, borderRadius: '0 4px 0 0',
        }} />
        <Box sx={{
          position: 'absolute', top: '12px', left: '14px',
          width: '18px', height: '18px',
          borderTop: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}`,
          opacity: 0.45, borderRadius: '4px 0 0 0',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '12px', right: '14px',
          width: '18px', height: '18px',
          borderBottom: `2px solid ${GOLD}`, borderRight: `2px solid ${GOLD}`,
          opacity: 0.45, borderRadius: '0 0 4px 0',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '12px', left: '14px',
          width: '18px', height: '18px',
          borderBottom: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}`,
          opacity: 0.45, borderRadius: '0 0 0 4px',
        }} />

        {/* Card content — centered vertically */}
        <Box sx={{
          flex: 1, minHeight: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          px: 4, py: 3, gap: 1.5, textAlign: 'center',
        }}>

          {/* Slot type label */}
          <Typography sx={{
            color: GOLD, fontWeight: 700, fontSize: '0.72em', letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}>
            {slotLabel || 'פרנס הקהילה'}
          </Typography>

          {/* Context (parsha / hebrew date) */}
          {contextLine && (
            <Typography sx={{
              color: heritageColors.slate, fontSize: '0.78em', opacity: 0.7,
            }}>
              {contextLine}
            </Typography>
          )}

          {/* Decorative line */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, width: '70%',
          }}>
            <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(to right, transparent, ${GOLD})`, opacity: 0.5 }} />
            <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', bgcolor: GOLD, opacity: 0.6 }} />
            <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(to left, transparent, ${GOLD})`, opacity: 0.5 }} />
          </Box>

          {/* Sponsor name — very prominent */}
          <Typography sx={{
            color:      sponsor ? heritageColors.navy : heritageColors.slate,
            fontWeight: 800,
            fontSize:   sponsor ? '2.2em' : '1.4em',
            lineHeight: 1.2,
            opacity:    sponsor ? 1 : 0.25,
            wordBreak:  'break-word',
          }}>
            {sponsor ? sponsor.sponsorName : placeholderName}
          </Typography>

          {!sponsor && (
            <Typography sx={{
              color: heritageColors.slate, fontSize: '0.78em', opacity: 0.45, lineHeight: 1.4,
            }}>
              ניתן לתרום — פנה לגבאי בית הכנסת
            </Typography>
          )}

          {/* Note — prominent and centered, unlike other templates */}
          {sponsor?.note && (
            <>
              {/* Decorative divider before note */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, width: '55%', mt: 0.5,
              }}>
                <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(to right, transparent, ${GOLD})`, opacity: 0.3 }} />
                <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: GOLD, opacity: 0.4 }} />
                <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(to left, transparent, ${GOLD})`, opacity: 0.3 }} />
              </Box>

              {/* Note text — significantly larger & more visible than in other templates */}
              <Typography sx={{
                color:      heritageColors.navy,
                fontWeight: 500,
                fontSize:   '1.05em',
                lineHeight: 1.5,
                opacity:    0.85,
                px:         1,
                wordBreak:  'break-word',
                textAlign:  'center',
                whiteSpace: 'pre-line',
              }}>
                {sponsor.note}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Footer */}
      {currentParshaName && (
        <>
          <Divider sx={{ borderColor: heritageColors.border, flexShrink: 0 }} />
          <Box sx={{ px: 2.5, py: 0.6, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: heritageColors.slate, fontSize: '0.78em' }}>
              {currentParshaName}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default HonorCard;
